import React from 'react';
import type { StreamAnalysis, SegmentResult, HLSVariant, StreamTestPhase } from '../types/diagnostics';

interface StreamAnalysisViewProps {
  phase: StreamTestPhase;
  analysis: StreamAnalysis | null;
  segments: SegmentResult[];
  variants: HLSVariant[];
  error: string | null;
}

export const StreamAnalysisView: React.FC<StreamAnalysisViewProps> = ({
  phase,
  analysis,
  segments,
  variants,
  error,
}) => {
  if (phase === 'idle' && !analysis) return null;

  const isActive = phase !== 'idle' && phase !== 'complete' && phase !== 'error';

  const getPhaseLabel = (p: StreamTestPhase) => {
    switch (p) {
      case 'connecting': return 'Подключение к потоку...';
      case 'parsing_playlist': return 'Разбор плейлиста...';
      case 'fetching_variant': return 'Загрузка варианта...';
      case 'testing_segments': return 'Тестирование сегментов...';
      case 'complete': return 'Завершено';
      case 'error': return 'Ошибка';
      default: return '';
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-yellow-400';
    if (score >= 40) return 'text-orange-400';
    return 'text-red-400';
  };

  const getScoreGrade = (score: number) => {
    if (score >= 90) return 'A';
    if (score >= 80) return 'B';
    if (score >= 60) return 'C';
    if (score >= 40) return 'D';
    return 'F';
  };

  const formatSpeed = (kbps: number) => {
    if (kbps >= 1000) return `${(kbps / 1000).toFixed(1)} Mbps`;
    return `${kbps.toFixed(0)} Kbps`;
  };

  const formatSize = (bytes: number) => {
    if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${bytes} B`;
  };

  const streamTypeLabel = (type: string) => {
    switch (type) {
      case 'hls': return 'HLS';
      case 'dash': return 'DASH';
      default: return 'Generic';
    }
  };

  return (
    <div className="space-y-4">
      {/* Progress */}
      {isActive && (
        <div className="bg-dark-800 rounded-xl p-4 border border-dark-700">
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm text-slate-300">{getPhaseLabel(phase)}</span>
            {segments.length > 0 && (
              <span className="text-xs text-slate-500 ml-auto">
                {segments.length} сегментов проверено
              </span>
            )}
          </div>
          {/* Segment download progress */}
          {segments.length > 0 && (
            <div className="mt-3 space-y-1.5">
              {segments.slice(-5).map((seg) => (
                <div key={seg.index} className="flex items-center gap-2 text-xs">
                  <span className="text-slate-500 w-6">#{seg.index + 1}</span>
                  <div className="flex-1 bg-dark-700 rounded-full h-1.5">
                    <div
                      className={`h-full rounded-full ${seg.error ? 'bg-red-500' : 'bg-blue-400'}`}
                      style={{ width: '100%' }}
                    />
                  </div>
                  <span className="text-slate-400 w-20 text-right">
                    {seg.error ? 'Error' : `${seg.downloadMs.toFixed(0)}ms`}
                  </span>
                  <span className="text-slate-500 w-20 text-right">
                    {seg.error ? '' : formatSpeed(seg.speedKbps)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Error */}
      {error && phase === 'error' && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Results */}
      {analysis && phase === 'complete' && (
        <div className="space-y-4">
          {/* Score & Status */}
          <div className="bg-dark-800 rounded-xl p-6 border border-dark-700">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                    analysis.available
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-red-500/20 text-red-400'
                  }`}>
                    {analysis.available ? '● Доступен' : '✕ Недоступен'}
                  </span>
                  <span className="text-xs text-slate-500 bg-dark-700 px-2 py-0.5 rounded">
                    {streamTypeLabel(analysis.streamType)}
                  </span>
                  {analysis.cdn && (
                    <span className="text-xs text-slate-500 bg-dark-700 px-2 py-0.5 rounded">
                      CDN: {analysis.cdn}
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-500 truncate max-w-lg mt-1" title={analysis.url}>
                  {analysis.url}
                </p>
              </div>
              <div className="text-right">
                <div className={`text-3xl font-bold ${getScoreColor(analysis.qualityScore)}`}>
                  {getScoreGrade(analysis.qualityScore)}
                </div>
                <div className="text-sm text-slate-400">{analysis.qualityScore.toFixed(1)}%</div>
              </div>
            </div>

            {/* Key metrics */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-5">
              <MetricCard label="TTFB" value={`${analysis.ttfbMs.toFixed(0)} ms`} />
              <MetricCard label="Битрейт" value={formatSpeed(analysis.bitrateKbps)} />
              <MetricCard label="Буфер" value={`${analysis.bufferHealthPct.toFixed(0)}%`} good={analysis.bufferHealthPct >= 80} />
              <MetricCard label="HTTP" value={`${analysis.statusCode}`} />
            </div>
          </div>

          {/* Variants */}
          {analysis.variants && analysis.variants.length > 0 && (
            <div className="bg-dark-800 rounded-xl p-5 border border-dark-700">
              <h3 className="text-sm font-semibold text-slate-300 mb-3">Качество потока (варианты)</h3>
              <div className="space-y-2">
                {analysis.variants.map((v, idx) => (
                  <div key={idx} className="flex items-center gap-3 text-xs bg-dark-700/50 rounded-lg px-3 py-2">
                    <span className="text-slate-400 w-14">{v.resolution || '—'}</span>
                    <div className="flex-1 bg-dark-600 rounded-full h-2">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-blue-500 to-cyan-400"
                        style={{ width: `${Math.min(100, (v.bandwidth / (analysis.variants![analysis.variants!.length - 1].bandwidth)) * 100)}%` }}
                      />
                    </div>
                    <span className="text-slate-300 w-24 text-right font-mono">{formatSpeed(v.bandwidth / 1000)}</span>
                    {v.codecs && <span className="text-slate-500 w-36 truncate">{v.codecs}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Segment Stats */}
          {analysis.segmentStats && (
            <div className="bg-dark-800 rounded-xl p-5 border border-dark-700">
              <h3 className="text-sm font-semibold text-slate-300 mb-3">Статистика сегментов</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatCard label="Всего" value={`${analysis.segmentStats.count}`} />
                <StatCard label="Ср. размер" value={`${analysis.segmentStats.avgSizeKB.toFixed(0)} KB`} />
                <StatCard label="Ср. TTFB" value={`${analysis.segmentStats.avgTtfbMs.toFixed(0)} ms`} />
                <StatCard label="Ср. скорость" value={formatSpeed(analysis.segmentStats.avgSpeedKbps)} />
                <StatCard label="Min загр." value={`${analysis.segmentStats.minDownloadMs.toFixed(0)} ms`} />
                <StatCard label="Max загр." value={`${analysis.segmentStats.maxDownloadMs.toFixed(0)} ms`} />
                <StatCard label="P90 загр." value={`${analysis.segmentStats.p90DownloadMs.toFixed(0)} ms`} />
                <StatCard label="Ошибки" value={`${analysis.segmentStats.errors}`} bad={analysis.segmentStats.errors > 0} />
              </div>
            </div>
          )}

          {/* Segments list */}
          {analysis.segments && analysis.segments.length > 0 && (
            <div className="bg-dark-800 rounded-xl p-5 border border-dark-700">
              <h3 className="text-sm font-semibold text-slate-300 mb-3">Тест сегментов</h3>
              <div className="space-y-1">
                {analysis.segments.map((seg) => (
                  <div key={seg.index} className="flex items-center gap-2 text-xs">
                    <span className="text-slate-500 w-8">#{seg.index + 1}</span>
                    <div className="flex-1">
                      <div className="bg-dark-700 rounded-full h-2">
                        <div
                          className={`h-full rounded-full transition-all ${
                            seg.error
                              ? 'bg-red-500'
                              : seg.downloadMs < 500
                                ? 'bg-green-400'
                                : seg.downloadMs < 1500
                                  ? 'bg-yellow-400'
                                  : 'bg-orange-400'
                          }`}
                          style={{
                            width: `${Math.min(
                              100,
                              seg.error
                                ? 100
                                : (seg.downloadMs / Math.max(...analysis.segments!.map((s) => s.downloadMs || 1))) * 100
                            )}%`,
                          }}
                        />
                      </div>
                    </div>
                    <span className="text-slate-400 w-16 text-right">
                      {seg.error ? 'Error' : `${seg.downloadMs.toFixed(0)} ms`}
                    </span>
                    <span className="text-slate-500 w-16 text-right">{formatSize(seg.sizeBytes)}</span>
                    <span className="text-slate-500 w-20 text-right">{seg.error ? '' : formatSpeed(seg.speedKbps)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Server info */}
          {(analysis.serverInfo || analysis.contentType) && (
            <div className="bg-dark-800 rounded-xl p-5 border border-dark-700">
              <h3 className="text-sm font-semibold text-slate-300 mb-3">Информация о сервере</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                {analysis.contentType && (
                  <InfoRow label="Content-Type" value={analysis.contentType} />
                )}
                {analysis.serverInfo && (
                  <InfoRow label="Server" value={analysis.serverInfo} />
                )}
                {analysis.cdn && (
                  <InfoRow label="CDN" value={analysis.cdn} />
                )}
                <InfoRow label="TTFB" value={`${analysis.ttfbMs.toFixed(1)} ms`} />
                <InfoRow label="Общее время" value={`${analysis.totalTimeMs.toFixed(0)} ms`} />
                <InfoRow label="Размер ответа" value={formatSize(analysis.bodySizeBytes)} />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const MetricCard: React.FC<{ label: string; value: string; good?: boolean }> = ({ label, value, good }) => (
  <div className="bg-dark-700/50 rounded-lg p-3">
    <div className="text-[10px] text-slate-500 uppercase tracking-wider">{label}</div>
    <div className={`text-lg font-bold mt-0.5 ${good !== undefined ? (good ? 'text-green-400' : 'text-orange-400') : 'text-white'}`}>
      {value}
    </div>
  </div>
);

const StatCard: React.FC<{ label: string; value: string; bad?: boolean }> = ({ label, value, bad }) => (
  <div className="bg-dark-700/50 rounded-lg px-3 py-2">
    <div className="text-[10px] text-slate-500">{label}</div>
    <div className={`text-sm font-semibold ${bad ? 'text-red-400' : 'text-white'}`}>{value}</div>
  </div>
);

const InfoRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex justify-between py-1 border-b border-dark-700 last:border-0">
    <span className="text-slate-500">{label}</span>
    <span className="text-slate-300 font-mono">{value}</span>
  </div>
);
